import React, { useState } from "react";

import Input from "@common/designSystem/Input";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { onboardingRouter } from "@editor/trpc/onboarding";
import { userUtils } from "@editor/trpc/user";

import twMerge from "@replo/design-system/utils/twMerge";
import { useNavigate, useSearchParams } from "react-router-dom";

import OnboardingFooter from "./OnboardingFooter";

const OnboardingUserInfo: React.FC = () => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [teamDescription, setTeamDescription] = useState(
    user?.profileType ?? "",
  );
  const [isUpdatingUserInfo, setIsUpdatingUserInfo] = useState(false);

  const { mutateAsync: updateBasicUserInfo } =
    onboardingRouter.updateBasicUserInfo.useMutation();

  const handleSubmit = async () => {
    if (teamDescription === "") {
      return;
    }

    setIsUpdatingUserInfo(true);
    try {
      await updateBasicUserInfo({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        teamDescription,
      });

      await userUtils.get.invalidate();

      logEvent("onboarding.flow.begin", {});
      if (user) {
        logEvent("onboarding.flow.step.completed", {
          userId: user.email,
          stepId: "user-profile",
          stepType: "user-profile",
          stepData: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            teamDescription,
          },
        });
      }

      const currentParams = searchParams.toString();
      const homeUrl = currentParams ? `/home?${currentParams}` : "/home";
      navigate(homeUrl);
    } finally {
      setIsUpdatingUserInfo(false);
    }
  };

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="flex-1 overflow-y-auto flex flex-col items-center md:py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="pt-14 md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-12 md:gap-3">
          <h1 className="header-h1 text-3xl md:text-4xl">
            Let's Get To Know You
          </h1>
          <p className="text-muted header-h1 text-lg font-normal">
            We'll use this info to create your user profile.
          </p>
        </div>
        <div className="md:w-[640px] w-full flex flex-col gap-10 pb-25 md:pb-40 px-5 md:px-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <label className="text-default text-sm md:text-base font-medium">
                First Name
              </label>
              <Input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                size="base"
                placeholder="Enter your first name"
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-default text-sm md:text-base font-medium">
                Last Name
              </label>
              <Input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                size="base"
                placeholder="Enter your last name"
              />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <label className="text-default text-sm md:text-base font-medium">
              What best describes you and your team?
            </label>
            <div className="flex flex-col gap-4 h-96">
              <div
                className="px-6 py-4 rounded-xl border-0.5 border-border-component bg-sheet flex-1 flex flex-col justify-center gap-2 relative cursor-pointer shadow-button-selectable"
                onClick={() => setTeamDescription("business")}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "business"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold relative z-10 transition-colors duration-300",
                      teamDescription !== "business" && "text-muted",
                    )}
                    style={
                      teamDescription === "business" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    Business
                  </h3>
                  <p
                    className={twMerge(
                      "text-sm relative z-10 transition-colors duration-300",
                      teamDescription !== "business" && "text-muted",
                    )}
                    style={
                      teamDescription === "business" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    Grow my business and reach more customers
                  </p>
                </div>
                <img
                  src="/images/onboarding/business-grey.png"
                  alt="Business"
                  className="h-35 w-35 object-contain absolute bottom-[-17px] right-10 z-10"
                />
                <img
                  src="/images/onboarding/business-color.png"
                  alt="Business"
                  className={twMerge(
                    "h-28 w-35 object-contain absolute bottom-[-4px] right-10 z-10 transition-opacity duration-300",
                    teamDescription === "business"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
              </div>
              <div
                className="px-6 py-4 rounded-xl border-0.5 border-border-component bg-sheet flex-1 flex flex-col justify-center gap-2 relative cursor-pointer shadow-button-selectable"
                onClick={() => setTeamDescription("agency")}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "agency" ? "opacity-100" : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold relative z-10 transition-colors duration-300",
                      teamDescription !== "agency" && "text-muted",
                    )}
                    style={
                      teamDescription === "agency" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    Freelancer or Agency
                  </h3>
                  <p
                    className={twMerge(
                      "text-sm relative z-10 transition-colors duration-300",
                      teamDescription !== "agency" && "text-muted",
                    )}
                    style={
                      teamDescription === "agency" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    I partner with clients to scale their businesses
                  </p>
                </div>
                <img
                  src="/images/onboarding/agency-grey.png"
                  alt="Agency"
                  className="h-30 w-30 object-contain absolute bottom-[-12px] right-12 z-10"
                />
                <img
                  src="/images/onboarding/agency-color.png"
                  alt="Agency"
                  className={twMerge(
                    "h-30 w-30 object-contain absolute bottom-[-20px] right-12 z-10 transition-opacity duration-300",
                    teamDescription === "agency" ? "opacity-100" : "opacity-0",
                  )}
                />
              </div>
              <div
                className="px-6 py-4 rounded-xl border-0.5 border-border-component bg-sheet flex-1 flex flex-col justify-center gap-2 relative cursor-pointer shadow-button-selectable"
                onClick={() => setTeamDescription("other")}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "other" ? "opacity-100" : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold relative z-10 transition-colors duration-300",
                      teamDescription !== "other" && "text-muted",
                    )}
                    style={
                      teamDescription === "other" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    Other
                  </h3>
                  <p
                    className={twMerge(
                      "text-sm relative z-10 transition-colors duration-300",
                      teamDescription !== "other" && "text-muted",
                    )}
                    style={
                      teamDescription === "other" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    I’m here to test ideas and see what’s possible with Replo
                  </p>
                </div>
                <img
                  src="/images/onboarding/freelancer-grey.png"
                  alt="Freelancer"
                  className="h-30 w-30 object-contain absolute bottom-[-10px] right-8 z-10"
                />
                <img
                  src="/images/onboarding/freelancer-color.png"
                  alt="Freelancer"
                  className={twMerge(
                    "h-30 w-30 object-contain absolute bottom-[-10px] right-8 z-10 transition-opacity duration-300",
                    teamDescription === "other" ? "opacity-100" : "opacity-0",
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20">
        <OnboardingFooter
          currentStep={1}
          onNext={handleSubmit}
          isNextDisabled={
            isUpdatingUserInfo ||
            firstName.trim() === "" ||
            teamDescription === ""
          }
          className="w-full md:max-w-[640px] px-5 md:px-0"
          isNextLoading={isUpdatingUserInfo}
          totalSteps={3}
        />
      </div>
    </div>
  );
};

export default OnboardingUserInfo;
